import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveViewId, clearActiveView } from '../../redux/slices/views';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bookmark, ChevronDown, BookmarkPlus } from 'lucide-react';
import { HiPencil, HiTrash, HiCheck, HiX } from 'react-icons/hi';
import CustomTooltip from '../../components/common/CustomTooltip';
import useViewsService from '../../services/views';
import { ROUTE_TO_COMPONENT, ComponentPage } from '../../constants/constants';
import { SavedViewsProps, View } from './SavedViews.def';
import SaveViewModal from './SaveViewModal';
import './SavedViews.css';

const SAVE_VIEW_DISABLED_TOOLTIP = (
  <div style={{ padding: '2px 4px', lineHeight: 1.6 }}>
    <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '11.5px' }}>
      Not available on this page
    </div>
    <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '6px' }}>
      You can only save views on pages that have<br />
      filters — go to one of these to use it:
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {['Work Items', 'Strategy Maps (list)', 'People (Capacity Planning)'].map((p) => (
        <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}>
          <span style={{ color: '#818cf8', fontWeight: 700 }}>•</span>
          <span>{p}</span>
        </div>
      ))}
    </div>
  </div>
);

const SAVE_VIEW_ENABLED_TOOLTIP = 'Save your current filters, sort & layout as a reusable view';

const COMPONENT_LABELS: Record<string, string> = {
  [ComponentPage.WORK_ITEMS]: 'Work Items',
  [ComponentPage.STRATEGY_MAPS]: 'Strategy Maps',
  [ComponentPage.EPIC]: 'Epics',
  [ComponentPage.FEATURE]: 'Features',
  [ComponentPage.PEOPLE]: 'People',
  [ComponentPage.NOTIFICATION]: 'Notifications',
  [ComponentPage.SETTINGS]: 'Settings',
};

const SavedViews: React.FC<SavedViewsProps> = ({ isCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchViews, deleteView } = useViewsService();
  const views: View[] = useSelector((state: { views: { views: View[] } }) => state.views.views);
  const viewsLoading: boolean = useSelector((state: { views: { loading: boolean } }) => state.views.loading);
  const activeViewId: number | null = useSelector((state: { views: { activeViewId: number | null } }) => state.views.activeViewId);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [editingView, setEditingView] = useState<View | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Determine current components from pathname (may be multiple per route)
  const currentComponents = useMemo((): ComponentPage[] => {
    const path = location.pathname;
    if (ROUTE_TO_COMPONENT[path]) return ROUTE_TO_COMPONENT[path];
    const matchingRoute = Object.keys(ROUTE_TO_COMPONENT)
      .filter(r => r !== '/')
      .sort((a, b) => b.length - a.length)
      .find(route => path.startsWith(route));
    return matchingRoute ? ROUTE_TO_COMPONENT[matchingRoute] : [];
  }, [location.pathname]);

  // Pages that have filter capture logic and can have views saved
  const SAVE_SUPPORTED_PAGES = [
    ComponentPage.WORK_ITEMS,
    ComponentPage.STRATEGY_MAPS,
    ComponentPage.PEOPLE,
  ];

  // Block detail/id pages — e.g. /strategymaps/:id should not allow saving
  const EXACT_ONLY_ROUTES = ['/strategymaps'];
  const isDetailPage = EXACT_ONLY_ROUTES.some(
    route => location.pathname.startsWith(route + '/') && location.pathname.length > route.length + 1
  );

  const canSaveView = !isDetailPage &&
    currentComponents.length > 0 &&
    currentComponents.some(c => SAVE_SUPPORTED_PAGES.includes(c));

  // Fetch all views (no component filter)
  useEffect(() => {
    fetchViews();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Toggle dropdown and compute position from button rect
  const handleToggleDropdown = useCallback(() => {
    if (!showDropdown && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.top - 80,
        left: rect.right + 8,
      });
    }
    setShowDropdown(!showDropdown);
  }, [showDropdown]);

  // Build current filters from URL search params for Work Items page
  const currentFilters = useMemo((): Record<string, string[]> => {
    const filters: Record<string, string[]> = {};
    const params = new URLSearchParams(location.search);

    // Work Items specific logic
    if (currentComponents.includes(ComponentPage.WORK_ITEMS)) {
      filters.type = [params.get('type') || localStorage.getItem('projects_searchType') || 'all'];
      const statusParam = params.get('status');
      if (statusParam) {
        filters.status = statusParam.split(',').filter(Boolean);
      } else {
        try {
          const statusLS = localStorage.getItem('projects_statusFilter');
          filters.status = statusLS ? JSON.parse(statusLS) : [];
        } catch { filters.status = []; }
      }
      filters.source = [params.get('source') || localStorage.getItem('projects_sourceFilter') || ''].filter(Boolean);
      filters.sort = [params.get('sort') || localStorage.getItem('projects_sortBy') || 'modified_at_desc'];
      filters.layout = [params.get('layout') || localStorage.getItem('projectsViewType') || 'list'];
      const quarterParam = params.get('quarter');
      if (quarterParam) {
        filters.quarter = quarterParam.split(',').filter(Boolean);
      } else {
        try {
          const quarterRaw = localStorage.getItem('projects_quarterFilter');
          filters.quarter = quarterRaw ? JSON.parse(quarterRaw) : [];
        } catch { filters.quarter = []; }
      }
      filters.pinned = [params.get('pinned') || localStorage.getItem('projects_pinnedFilter') || 'false'];
    }

    // Strategy Maps specific logic
    if (currentComponents.includes(ComponentPage.STRATEGY_MAPS)) {
      filters.type = [params.get('type') || localStorage.getItem('smap_searchMode') || 'name'];
      filters.sort = [params.get('sort') || localStorage.getItem('smap_sort') || 'modified_at_desc'];
      filters.source = [params.get('source') || localStorage.getItem('smap_source') || ''].filter(Boolean);
    }
    
    // People/Team Resource specific logic
    if (currentComponents.includes(ComponentPage.PEOPLE)) {
      filters.type = [params.get('type') || localStorage.getItem('res_searchType') || 'people'];
      filters.team = [params.get('team') || localStorage.getItem('res_selectedTeam') || ''];
      filters.sprint = [params.get('sprint') || localStorage.getItem('res_selectedSprint') || ''];
      filters.sprintSortBy = [params.get('sprintSortBy') || localStorage.getItem('res_sprintSortBy') || 'latest'];
      filters.sprintRange = [params.get('sprintRange') || localStorage.getItem('res_sprintRange') || 'current'];
      filters.sprintStatus = [params.get('sprintStatus') || localStorage.getItem('res_sprintStatus') || 'all'];
      filters.startDate = [params.get('startDate') || localStorage.getItem('res_startDate') || ''];
      filters.endDate = [params.get('endDate') || localStorage.getItem('res_endDate') || ''];
      
      const parseArray = (key: string) => {
        const param = params.get(key);
        if (param) return param.split(',').filter(Boolean);
        try {
          const raw = localStorage.getItem(`res_selected${key.charAt(0).toUpperCase() + key.slice(1)}`);
          return raw ? JSON.parse(raw) : [];
        } catch { return []; }
      };
      
      filters.resources = parseArray('resources');
      filters.skills = parseArray('skills');
      filters.teams = parseArray('teams');
      
      const statusParam = params.get('status');
      if (statusParam) {
        filters.status = statusParam.split(',').filter(Boolean);
      } else {
        try {
          const statusRaw = localStorage.getItem('res_status');
          filters.status = statusRaw ? JSON.parse(statusRaw) : [];
        } catch { filters.status = []; }
      }
      
      const sprintSkillsParam = params.get('sprintSkills');
      if (sprintSkillsParam) {
        filters.sprintSkills = sprintSkillsParam.split(',').filter(Boolean);
      } else {
        try {
          const raw = localStorage.getItem('res_sprintSkills');
          filters.sprintSkills = raw ? JSON.parse(raw) : [];
        } catch { filters.sprintSkills = []; }
      }

      const sprintWorkItemsParam = params.get('sprintWorkItems');
      if (sprintWorkItemsParam) {
        filters.sprintWorkItems = sprintWorkItemsParam.split(',').filter(Boolean);
      } else {
        try {
          const raw = localStorage.getItem('res_sprintWorkItems');
          filters.sprintWorkItems = raw ? JSON.parse(raw) : [];
        } catch { filters.sprintWorkItems = []; }
      }
    }

    return filters;
  }, [currentComponents, showSaveModal, location.search]); // re-compute when modal opens or URL changes

  // Clear active highlight when user manually changes filters on the SAME page.
  // Do NOT clear when navigating to a different page — keep activeViewId so the
  // restore handler in SideNav can bring back pre-view filters.
  useEffect(() => {
    if (activeViewId == null) return;
    const activeView = views.find(v => v.id === activeViewId);
    if (!activeView) { dispatch(setActiveViewId(null)); return; }

    // Only check filter match if we're on the view's page
    if (!currentComponents.includes(activeView.component as ComponentPage)) return;

    const config = activeView.config || {};
    const filtersMatch = Object.keys(config).every(key => {
      const saved = [...(config[key] || [])].sort().join(',');
      const current = [...(currentFilters[key] || [])].sort().join(',');
      return saved === current;
    });

    if (!filtersMatch) dispatch(setActiveViewId(null));
  }, [activeViewId, views, location.pathname, location.search, currentFilters, currentComponents]);

  const handleSaveSuccess = () => {
    fetchViews();
  };

  const handleDeleteView = async (viewId: number) => {
    await deleteView(viewId);
    if (activeViewId === viewId) dispatch(clearActiveView());
  };

  const handleApplyView = (view: View) => {
    const config = view.config || {};

    // Map component to route
    const componentRoutes: Record<string, string> = {
      [ComponentPage.WORK_ITEMS]: '/',
      [ComponentPage.STRATEGY_MAPS]: '/strategymaps',
      [ComponentPage.EPIC]: '/projects',
      [ComponentPage.FEATURE]: '/projects',
      [ComponentPage.PEOPLE]: '/resources',
      [ComponentPage.NOTIFICATION]: '/notification',
      [ComponentPage.SETTINGS]: '/settings',
    };

    // The useUrlFilters hook auto-syncs filters to Redux preViewFilters.
    // When we set activeViewId, the hook freezes (stops writing), preserving the pre-view state.
    dispatch(setActiveViewId(view.id));

    const targetRoute = componentRoutes[view.component] || '/';
    const params = new URLSearchParams();

    // Build URL with query params from saved config
    if (view.component === ComponentPage.WORK_ITEMS) {
      if (config.type?.[0] && config.type[0] !== 'all') params.set('type', config.type[0]);
      if (config.status?.length) params.set('status', config.status.join(','));
      if (config.source?.[0]) params.set('source', config.source[0]);
      if (config.sort?.[0] && config.sort[0] !== 'modified_at_desc') params.set('sort', config.sort[0]);
      if (config.layout?.[0] && config.layout[0] !== 'list') params.set('layout', config.layout[0]);
      if (config.quarter?.length) params.set('quarter', config.quarter.join(','));
      if (config.pinned?.[0] && config.pinned[0] !== 'false') params.set('pinned', config.pinned[0]);

      // Also update localStorage so filters persist
      if (config.type?.[0]) localStorage.setItem('projects_searchType', config.type[0]);
      if (config.sort?.[0]) localStorage.setItem('projects_sortBy', config.sort[0]);
      localStorage.setItem('projects_sourceFilter', config.source?.[0] || '');
      localStorage.setItem('projects_pinnedFilter', config.pinned?.[0] || 'false');
      localStorage.setItem('projects_statusFilter', JSON.stringify(config.status || []));
      localStorage.setItem('projects_quarterFilter', JSON.stringify(config.quarter || []));
      if (config.layout?.[0]) localStorage.setItem('projectsViewType', config.layout[0]);
    } else if (view.component === ComponentPage.STRATEGY_MAPS) {
      if (config.type?.[0] && config.type[0] !== 'name') params.set('type', config.type[0]);
      if (config.sort?.[0] && config.sort[0] !== 'modified_at_desc') params.set('sort', config.sort[0]);
      if (config.source?.[0]) params.set('source', config.source[0]);

      // Also update localStorage so filters persist
      if (config.type?.[0]) localStorage.setItem('smap_searchMode', config.type[0]);
      if (config.sort?.[0]) localStorage.setItem('smap_sort', config.sort[0]);
      localStorage.setItem('smap_source', config.source?.[0] || '');
    } else if (view.component === ComponentPage.PEOPLE) {
      // Capacity utilization filters
      if (config.type?.[0] && config.type[0] !== 'people') params.set('type', config.type[0]);
      if (config.team?.[0]) params.set('team', config.team[0]);
      if (config.sprint?.[0]) params.set('sprint', config.sprint[0]);
      if (config.sprintSortBy?.[0] && config.sprintSortBy[0] !== 'latest') params.set('sprintSortBy', config.sprintSortBy[0]);
      if (config.sprintRange?.[0] && config.sprintRange[0] !== 'current') params.set('sprintRange', config.sprintRange[0]);
      if (config.sprintStatus?.[0] && config.sprintStatus[0] !== 'all') params.set('sprintStatus', config.sprintStatus[0]);
      if (config.startDate?.[0]) params.set('startDate', config.startDate[0]);
      if (config.endDate?.[0]) params.set('endDate', config.endDate[0]);
      
      if (config.resources?.length) params.set('resources', config.resources.join(','));
      if (config.skills?.length) params.set('skills', config.skills.join(','));
      if (config.teams?.length) params.set('teams', config.teams.join(','));
      if (config.status?.length) params.set('status', config.status.join(','));
      if (config.sprintSkills?.length) params.set('sprintSkills', config.sprintSkills.join(','));
      if (config.sprintWorkItems?.length) params.set('sprintWorkItems', config.sprintWorkItems.join(','));

      // Remove old Manage page localStorage sets here
      // Capacity utilization localStorage
      if (config.type?.[0]) localStorage.setItem('res_searchType', config.type[0]);
      localStorage.setItem('res_selectedTeam', config.team?.[0] || '');
      localStorage.setItem('res_selectedSprint', config.sprint?.[0] || '');
      if (config.sprintSortBy?.[0]) localStorage.setItem('res_sprintSortBy', config.sprintSortBy[0]);
      if (config.sprintRange?.[0]) localStorage.setItem('res_sprintRange', config.sprintRange[0]);
      if (config.sprintStatus?.[0]) localStorage.setItem('res_sprintStatus', config.sprintStatus[0]);
      localStorage.setItem('res_startDate', config.startDate?.[0] || '');
      localStorage.setItem('res_endDate', config.endDate?.[0] || '');
      
      localStorage.setItem('res_selectedResources', JSON.stringify(config.resources || []));
      localStorage.setItem('res_selectedSkills', JSON.stringify(config.skills || []));
      localStorage.setItem('res_selectedTeams', JSON.stringify(config.teams || []));
      localStorage.setItem('res_status', JSON.stringify(config.status || []));
      localStorage.setItem('res_sprintSkills', JSON.stringify(config.sprintSkills || []));
      localStorage.setItem('res_sprintWorkItems', JSON.stringify(config.sprintWorkItems || []));
    }

    const queryString = params.toString();
    const fullPath = queryString ? `${targetRoute}?${queryString}` : targetRoute;
    navigate(fullPath);
  };

  const renderViewsList = () => {
    if (viewsLoading) {
      return <div className="saved-views-loading">Loading...</div>;
    }
    if (views.length === 0) {
      return (
        <div className="saved-views-empty-state">
          <Bookmark className="saved-views-empty-icon" />
          <p className="saved-views-empty-text">No saved views yet</p>
          <p className="saved-views-empty-sub">Save your current filters to create a view</p>
        </div>
      );
    }

    // Group views by component
    const grouped: Record<string, View[]> = {};
    views.forEach((view) => {
      const key = view.component || 'other';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(view);
    });

    const toggleGroup = (groupKey: string) => {
      setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    return (
      <>
        {Object.entries(grouped).map(([component, groupViews]) => {
          const isGroupCollapsed = !!collapsedGroups[component];
          return (
          <div key={component} className="saved-views-group">
            <div
              className="saved-views-group-header saved-views-group-header--clickable"
              onClick={() => toggleGroup(component)}
            >
              <ChevronDown
                className={`saved-views-group-chevron ${isGroupCollapsed ? 'saved-views-group-chevron--collapsed' : ''}`}
              />
              <span className="saved-views-group-label">
                {COMPONENT_LABELS[component] || component}
              </span>
              <span className="saved-views-group-count">{groupViews.length}</span>
            </div>
            <div className={`saved-views-group-items ${isGroupCollapsed ? 'saved-views-group-items--collapsed' : ''}`}>
            {groupViews.map((view: View) => {
              const isActive = activeViewId === view.id && currentComponents.includes(view.component as ComponentPage);
              return (
              <div
                key={view.id}
                className={`saved-views-item ${isActive ? 'saved-views-item--active' : ''}`}
                onClick={() => handleApplyView(view)}
              >
                <Bookmark className={`saved-views-item-icon ${isActive ? 'saved-views-item-icon--active' : ''}`} fill={isActive ? 'currentColor' : 'none'} />
                <div className="saved-views-item-content">
                  <span className="saved-views-item-name">{view.name}</span>
                  {view.description && (
                    <p className="saved-views-item-desc">{view.description}</p>
                  )}
            </div>
            <div className="saved-views-item-actions">
              {confirmDeleteId === view.id ? (
                /* Confirm / Cancel delete */
                <>
                  <button
                    className="saved-views-action-btn saved-views-action-confirm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteView(view.id);
                      setConfirmDeleteId(null);
                    }}
                    title="Confirm delete"
                  >
                    <HiCheck className="saved-views-action-icon" />
                  </button>
                  <button
                    className="saved-views-action-btn saved-views-action-cancel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(null);
                    }}
                    title="Cancel"
                  >
                    <HiX className="saved-views-action-icon" />
                  </button>
                </>
              ) : (
                /* Edit / Delete buttons */
                <>
                  <button
                    className="saved-views-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingView(view);
                      setShowSaveModal(true);
                    }}
                    title="Edit view"
                  >
                    <HiPencil className="saved-views-action-icon" />
                  </button>
                  <button
                    className="saved-views-action-btn saved-views-action-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(view.id);
                    }}
                    title="Delete view"
                  >
                    <HiTrash className="saved-views-action-icon" />
                  </button>
                </>
              )}
            </div>
          </div>
              );
            })}
            </div>
          </div>
          );
        })}
      </>
    );
  };

  // Portal-rendered dropdown for collapsed mode
  const dropdownPortal = showDropdown
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="saved-views-dropdown"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          <div className="saved-views-dropdown-header">
            <h3 className="saved-views-title">Saved Views</h3>
            <CustomTooltip
              title={canSaveView ? SAVE_VIEW_ENABLED_TOOLTIP : SAVE_VIEW_DISABLED_TOOLTIP}
              placement="right"
              arrow
            >
              {canSaveView ? (
                <button
                  className="saved-views-save-btn"
                  onClick={() => {
                    setShowSaveModal(true);
                    setShowDropdown(false);
                  }}
                >
                  <BookmarkPlus className="saved-views-save-btn-icon" />
                  <span>Save View</span>
                </button>
              ) : (
                <span className="saved-views-save-btn-wrap">
                  <button
                    className="saved-views-save-btn saved-views-save-btn--disabled"
                    disabled
                    tabIndex={-1}
                  >
                    <BookmarkPlus className="saved-views-save-btn-icon" />
                    <span>Save View</span>
                  </button>
                </span>
              )}
            </CustomTooltip>
          </div>
          <div className="saved-views-dropdown-list">
            {renderViewsList()}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="saved-views-section">
        {!isCollapsed && (
          <>
            <div className="saved-views-header">
              <h3 className="saved-views-title">Saved Views</h3>
              <CustomTooltip
                title={canSaveView ? SAVE_VIEW_ENABLED_TOOLTIP : SAVE_VIEW_DISABLED_TOOLTIP}
                placement="right"
                arrow
              >
                {canSaveView ? (
                  <button
                    className="saved-views-save-btn"
                    onClick={() => setShowSaveModal(true)}
                  >
                    <BookmarkPlus className="saved-views-save-btn-icon" />
                    <span>Save View</span>
                  </button>
                ) : (
                  <span className="saved-views-save-btn-wrap">
                    <button
                      className="saved-views-save-btn saved-views-save-btn--disabled"
                      disabled
                      tabIndex={-1}
                    >
                      <BookmarkPlus className="saved-views-save-btn-icon" />
                      <span>Save View</span>
                    </button>
                  </span>
                )}
              </CustomTooltip>
            </div>
            <div className="saved-views-list">
              {renderViewsList()}
            </div>
          </>
        )}

        {isCollapsed && (
          <div className="saved-views-collapsed-wrapper">
            <button
              ref={btnRef}
              className="saved-views-collapsed-btn"
              onClick={handleToggleDropdown}
              title="Saved Views"
            >
              <Bookmark className="saved-views-collapsed-icon" />
            </button>
            {dropdownPortal}
          </div>
        )}
      </div>

      <SaveViewModal
        isVisible={showSaveModal}
        component={currentComponents[0]}
        editingView={editingView}
        currentFilters={currentFilters}
        onClose={() => {
          setShowSaveModal(false);
          setEditingView(null);
        }}
        onSuccess={handleSaveSuccess}
      />
    </>
  );
};

export default SavedViews;
